import { useState, useEffect, useRef } from "react";
import { X, Users, Calendar, FileText, Edit, Save, Building, User, Key, MapPin } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Textarea } from "../../../../components/ui/textarea";
import { useAuth } from "../../../../context/AuthContext";
import {
  AsambleaRecord,
  obtenerAsambleas,
  modificarAsamblea,
  modificarEstatusAsamblea,
} from "../../../../api/asambleaService";
import { obtenerTorres, Torre } from "../../../../api/torreService";
import { obtenerApartamentos, Apartamento } from "../../../../api/apartamentoService";
import { obtenerResidentes, Residente } from "../../../../api/residenteService";
import { obtenerEstatusPreguntaAsamblea, EstatusPreguntaAsamblea } from "../../../../api/estatusPreguntaAsambleaService";
import Swal from "sweetalert2";

interface DetailAsambleaModalProps {
  isOpen: boolean;
  asamblea: AsambleaRecord | null;
  onClose: (shouldReload?: boolean) => void;
}

const formatDate = (value: string) => {
  if (!value || value.trim() === "") return "Sin fecha";
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return date.toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
};

export default function DetailAsambleaModal({
  isOpen,
  asamblea,
  onClose,
}: DetailAsambleaModalProps) {
  const { session } = useAuth();
  const lastLoadedAsambleaRef = useRef<string>("");
  const isLoadingRef = useRef(false);

  const [isEditing, setIsEditing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [asambleaData, setAsambleaData] = useState<AsambleaRecord | null>(asamblea);

  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [selectedTorre, setSelectedTorre] = useState("");
  const [selectedApartamento, setSelectedApartamento] = useState("");
  const [selectedModerador, setSelectedModerador] = useState("");
  const [selectedEstatus, setSelectedEstatus] = useState("");

  const [torres, setTorres] = useState<Torre[]>([]);
  const [apartamentos, setApartamentos] = useState<Apartamento[]>([]);
  const [residentes, setResidentes] = useState<Residente[]>([]);
  const [estatusList, setEstatusList] = useState<EstatusPreguntaAsamblea[]>([]);

  const [isLoadingTorres, setIsLoadingTorres] = useState(false);
  const [isLoadingApartamentos, setIsLoadingApartamentos] = useState(false);
  const [isLoadingResidentes, setIsLoadingResidentes] = useState(false);
  const [isLoadingEstatus, setIsLoadingEstatus] = useState(false);

  const loadAsambleaDetails = async () => {
    if (!asamblea?.asa_codigo || !session?.unidad?.id) return;

    try {
      setIsLoadingDetails(true);
      const response = await obtenerAsambleas({
        "unidad residencial codigo": session.unidad.id,
        codigo: String(asamblea.asa_codigo),
      });

      if (response.code === "1" && Array.isArray(response.object_) && response.object_.length > 0) {
        const data = response.object_[0];
        setAsambleaData(data);
        setTitulo(data.asa_titulo);
        setDescripcion(data.asa_descripcion || "");
        setSelectedEstatus(data.asa_estatus || "");
        setSelectedModerador(data.asa_moderador || "");
      }
    } catch (error) {
      console.error("Error al cargar detalles de asamblea:", error);
    } finally {
      setIsLoadingDetails(false);
    }
  };

  useEffect(() => {
    if (isOpen && asamblea) {
      const asambleaId = String(asamblea.asa_codigo || "");
      const isNewAsamblea = lastLoadedAsambleaRef.current !== asambleaId;

      if (isNewAsamblea && !isLoadingRef.current) {
        lastLoadedAsambleaRef.current = asambleaId;
        loadAsambleaDetails().then(() => {
          loadCatalogos();
        });
      } else if (!isNewAsamblea) {
        setAsambleaData(asamblea);
        setTitulo(asamblea.asa_titulo);
        setDescripcion(asamblea.asa_descripcion || "");
        setSelectedEstatus(asamblea.asa_estatus || "");
        setSelectedModerador(asamblea.asa_moderador || "");
      }
    } else {
      setTorres([]);
      setApartamentos([]);
      setResidentes([]);
      setEstatusList([]);
      lastLoadedAsambleaRef.current = "";
    }
  }, [isOpen, asamblea?.asa_codigo]);

  const loadCatalogos = async () => {
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) return;

      setIsLoadingTorres(true);
      setIsLoadingEstatus(true);

      const torresResponse = await obtenerTorres({
        "unidad residencial codigo": unidadCodigo,
      });

      if (torresResponse.code === "1" && torresResponse.object_) {
        setTorres(torresResponse.object_);
      }
      setIsLoadingTorres(false);

      const estatusResponse = await obtenerEstatusPreguntaAsamblea();
      if (estatusResponse.code === "1" && estatusResponse.object_) {
        setEstatusList(estatusResponse.object_);
      }
      setIsLoadingEstatus(false);

      if (asambleaData?.asa_moderador) {
        await loadResidentesForModerador();
      }
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
    } finally {
      isLoadingRef.current = false;
    }
  };

  const loadResidentesForModerador = async () => {
    if (!asambleaData?.asa_moderador || !session?.unidad?.id) return;

    try {
      const unidadCodigo = session.unidad.id;
      const response = await obtenerResidentes({
        "correo electronico usuario": "",
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": "",
        "apartamento codigo": "",
        "tipo usuario codigo": "",
      });

      if (response.code === "1" && response.object_) {
        const moderador = response.object_.find(
          (r) => r.res_email === asambleaData.asa_moderador
        );
        if (moderador) {
          const torreCodigo = moderador.res_torre || "";
          const aptCodigo = moderador.res_apartamento || "";
          setSelectedTorre(torreCodigo);
          setSelectedApartamento(aptCodigo);
          setSelectedModerador(asambleaData.asa_moderador);
          if (torreCodigo) {
            await loadApartamentos(torreCodigo);
            if (aptCodigo) {
              await loadResidentes(aptCodigo);
            }
          }
        }
      }
    } catch (error) {
      console.error("Error al cargar moderador:", error);
    }
  };

  const loadApartamentos = async (torreCodigo: string) => {
    if (!torreCodigo || torreCodigo.trim() === "") {
      setApartamentos([]);
      setResidentes([]);
      setSelectedApartamento("");
      setSelectedModerador("");
      return;
    }

    try {
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) return;

      setIsLoadingApartamentos(true);
      const response = await obtenerApartamentos({
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": torreCodigo,
      });

      if (response.code === "1" && response.object_) {
        setApartamentos(response.object_);
        if (asambleaData?.asa_moderador && !selectedApartamento) {
          const unidadCodigo = session?.unidad?.id || "";
          const allResidentes = await obtenerResidentes({
            "correo electronico usuario": "",
            "unidad residencial codigo": unidadCodigo,
            "torre codigo": torreCodigo,
            "apartamento codigo": "",
            "tipo usuario codigo": "",
          });
          if (allResidentes.code === "1" && allResidentes.object_) {
            const mod = allResidentes.object_.find((r) => r.res_email === asambleaData.asa_moderador);
            if (mod?.res_apartamento) {
              setSelectedApartamento(mod.res_apartamento);
              await loadResidentes(mod.res_apartamento);
            }
          }
        } else if (selectedApartamento) {
          await loadResidentes(selectedApartamento);
        }
      } else {
        setApartamentos([]);
      }
    } catch (error) {
      console.error("Error al cargar apartamentos:", error);
      setApartamentos([]);
    } finally {
      setIsLoadingApartamentos(false);
    }
  };

  const loadResidentes = async (apartamentoCodigo: string) => {
    if (!apartamentoCodigo || apartamentoCodigo.trim() === "") {
      setResidentes([]);
      if (asambleaData?.asa_moderador !== selectedModerador) {
        setSelectedModerador("");
      }
      return;
    }

    try {
      const unidadCodigo = session?.unidad?.id || "";
      const torreCodigo = selectedTorre || "";
      if (!unidadCodigo || !torreCodigo) return;

      setIsLoadingResidentes(true);
      const response = await obtenerResidentes({
        "correo electronico usuario": "",
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": torreCodigo,
        "apartamento codigo": apartamentoCodigo,
        "tipo usuario codigo": "",
      });

      if (response.code === "1" && response.object_) {
        setResidentes(response.object_);
        if (asambleaData?.asa_moderador && !selectedModerador) {
          const moderador = response.object_.find(
            (r) => r.res_email === asambleaData.asa_moderador
          );
          if (moderador) {
            setSelectedModerador(moderador.res_email || "");
          }
        }
      } else {
        setResidentes([]);
      }
    } catch (error) {
      console.error("Error al cargar residentes:", error);
      setResidentes([]);
    } finally {
      setIsLoadingResidentes(false);
    }
  };

  const handleTorreChange = (value: string) => {
    setSelectedTorre(value);
    setSelectedApartamento("");
    setSelectedModerador("");
    setApartamentos([]);
    setResidentes([]);
    if (value) {
      loadApartamentos(value);
    }
  };

  const handleApartamentoChange = (value: string) => {
    setSelectedApartamento(value);
    setSelectedModerador("");
    setResidentes([]);
    if (value) {
      loadResidentes(value);
    }
  };

  const handleSaveChanges = async () => {
    if (!titulo.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese el título de la asamblea",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!descripcion.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor ingrese la descripción de la asamblea",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    if (!selectedModerador) {
      await Swal.fire({
        icon: "warning",
        title: "Campo requerido",
        text: "Por favor seleccione un moderador",
        confirmButtonColor: "#3d5a40",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const unidadCodigo = session?.unidad?.id || "";
      const usuarioAdministrador = session?.email || "";
      const codigo = String(asambleaData?.asa_codigo || "");

      if (!unidadCodigo || !usuarioAdministrador || !codigo) {
        throw new Error("No se pudo obtener la información necesaria");
      }

      const payload = {
        codigo,
        unidad_residencial_codigo: unidadCodigo,
        usuario_administrador: usuarioAdministrador,
        usuario_moderador: selectedModerador,
        titulo: titulo.trim(),
        descripcion: descripcion.trim(),
      };

      const response = await modificarAsamblea(payload);

      if (response.code === "1" || response.code === 1) {
        await Swal.fire({
          icon: "success",
          title: "¡Cambios guardados!",
          text: response.message || "La asamblea se ha actualizado exitosamente",
          confirmButtonColor: "#3d5a40",
          timer: 2000,
          timerProgressBar: true,
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
        setIsEditing(false);
        await loadAsambleaDetails();
        onClose(true);
      } else {
        throw new Error(response.message || "Error al actualizar la asamblea");
      }
    } catch (error) {
      console.error("Error al guardar cambios:", error);
      const errorMessage = error instanceof Error ? error.message : "Error al actualizar la asamblea";
      const mensajeTraducido = errorMessage.includes("Request failed")
        ? "Error al actualizar la asamblea. Por favor, intente nuevamente."
        : errorMessage.includes("Network Error")
        ? "Error de conexión. Verifique su conexión a internet."
        : errorMessage;

      await Swal.fire({
        icon: "error",
        title: "Error",
        text: mensajeTraducido,
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEstatusChange = async (estatusCodigo: string) => {
    const previousEstatus = selectedEstatus;
    setSelectedEstatus(estatusCodigo);

    try {
      const unidadCodigo = session?.unidad?.id || "";
      const usuarioAdministrador = session?.email || "";
      const codigo = String(asambleaData?.asa_codigo || "");

      if (!unidadCodigo || !usuarioAdministrador || !codigo) {
        throw new Error("No se pudo obtener la información necesaria");
      }

      const payload = {
        codigo,
        unidad_residencial_codigo: unidadCodigo,
        usuario_administrador: usuarioAdministrador,
        estatus_codigo: estatusCodigo,
      };

      const response = await modificarEstatusAsamblea(payload);

      if (response.code !== "1" && response.code !== 1) {
        setSelectedEstatus(previousEstatus);
        await Swal.fire({
          icon: "error",
          title: "Error",
          text: response.message || "No se pudo cambiar el estatus",
          confirmButtonColor: "#d33",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
      } else {
        await loadAsambleaDetails();
      }
    } catch (error) {
      setSelectedEstatus(previousEstatus);
      console.error("Error al cambiar estatus:", error);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cambiar el estatus",
        confirmButtonColor: "#d33",
        didOpen: (popup) => {
          (popup as HTMLElement).style.zIndex = '10001';
        },
      });
    }
  };

  if (!isOpen || !asamblea) return null;

  const asambleaMostrar = asambleaData || asamblea;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        <div className="bg-white border-b px-6 py-4 flex items-center justify-between flex-shrink-0">
          <div>
            <h2 className="text-2xl font-bold text-military-600 flex items-center gap-2">
              <Users size={28} />
              Detalle de Asamblea
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Código: #{asambleaMostrar.asa_codigo}
            </p>
          </div>
          <button
            onClick={() => onClose(false)}
            className="text-gray-400 hover:text-gray-600 transition"
            disabled={isSubmitting || isLoadingDetails}
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto flex-1">
          {isLoadingDetails ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
              <p className="text-gray-600">Cargando detalles...</p>
            </div>
          ) : (
            <>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estatus
                </label>
                {isLoadingEstatus ? (
                  <div className="flex items-center gap-2 text-gray-500">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-military-500 border-gray-300" />
                    <span className="text-sm">Cargando estatus...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {estatusList.map((estatus) => (
                      <label
                        key={estatus.eps_codigo}
                        className="flex items-center gap-2 p-2 rounded-lg border border-gray-200 hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="estatus"
                          value={estatus.eps_codigo}
                          checked={selectedEstatus === estatus.eps_codigo}
                          onChange={(e) => handleEstatusChange(e.target.value)}
                          disabled={isSubmitting}
                          className="text-military-500 focus:ring-military-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          {estatus.eps_nombre}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileText size={16} className="inline mr-1" />
                    Título <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <Input
                      type="text"
                      value={titulo}
                      onChange={(e) => setTitulo(e.target.value)}
                      disabled={isSubmitting}
                      required
                    />
                  ) : (
                    <Input value={asambleaMostrar.asa_titulo} disabled className="bg-gray-50" />
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <FileText size={16} className="inline mr-1" />
                    Descripción <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <Textarea
                      value={descripcion}
                      onChange={(e) => setDescripcion(e.target.value)}
                      disabled={isSubmitting}
                      className="min-h-[120px]"
                      required
                    />
                  ) : (
                    <Textarea
                      value={asambleaMostrar.asa_descripcion || "Sin descripción"}
                      disabled
                      className="bg-gray-50 min-h-[120px]"
                    />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Key size={16} className="inline mr-1" />
                    Llave de Asamblea
                  </label>
                  <Input
                    value={asambleaMostrar.asa_llave}
                    disabled
                    className="bg-gray-50 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building size={16} className="inline mr-1" />
                    Unidad Residencial
                  </label>
                  <Input
                    value={asambleaMostrar.asa_unidad_residencial_nombre}
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {asambleaMostrar.asa_unidad_residencial}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar size={16} className="inline mr-1" />
                    Fecha de Inicio
                  </label>
                  <Input
                    value={formatDate(asambleaMostrar.asa_fecha_inicio) || "Sin fecha"}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Calendar size={16} className="inline mr-1" />
                    Fecha de Finalización
                  </label>
                  <Input
                    value={formatDate(asambleaMostrar.asa_fecha_cierre) || "Sin fecha"}
                    disabled
                    className="bg-gray-50"
                  />
                </div>

                <div className="md:col-span-2 border-t pt-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Responsables
                  </h3>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User size={16} className="inline mr-1" />
                    Administrador
                  </label>
                  <Input
                    value={asambleaMostrar.asa_administrador_nombre}
                    disabled
                    className="bg-gray-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {asambleaMostrar.asa_administrador}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <User size={16} className="inline mr-1" />
                    Moderador <span className="text-red-500">*</span>
                  </label>
                  {isEditing ? (
                    <>
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Torre
                        </label>
                        {isLoadingTorres ? (
                          <div className="flex items-center gap-2 text-gray-500">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-military-500 border-gray-300" />
                            <span className="text-sm">Cargando...</span>
                          </div>
                        ) : (
                          <select
                            value={selectedTorre}
                            onChange={(e) => handleTorreChange(e.target.value)}
                            disabled={isSubmitting || isLoadingTorres}
                            className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                          >
                            <option value="">Seleccione una torre</option>
                            {torres.map((torre) => (
                              <option key={torre.tor_codigo} value={torre.tor_codigo}>
                                {torre.tor_nombre}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div className="mb-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Apartamento
                        </label>
                        {isLoadingApartamentos ? (
                          <div className="flex items-center gap-2 text-gray-500">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-military-500 border-gray-300" />
                            <span className="text-sm">Cargando...</span>
                          </div>
                        ) : (
                          <select
                            value={selectedApartamento}
                            onChange={(e) => handleApartamentoChange(e.target.value)}
                            disabled={isSubmitting || isLoadingApartamentos || !selectedTorre}
                            className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                          >
                            <option value="">
                              {selectedTorre ? "Seleccione un apartamento" : "Primero seleccione una torre"}
                            </option>
                            {apartamentos.map((apt) => (
                              <option key={apt.apt_codigo} value={apt.apt_codigo}>
                                {apt.apt_nombre}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Moderador
                        </label>
                        {isLoadingResidentes ? (
                          <div className="flex items-center gap-2 text-gray-500">
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-t-military-500 border-gray-300" />
                            <span className="text-sm">Cargando...</span>
                          </div>
                        ) : (
                          <select
                            value={selectedModerador}
                            onChange={(e) => setSelectedModerador(e.target.value)}
                            disabled={isSubmitting || isLoadingResidentes || !selectedApartamento}
                            className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                            required
                          >
                            <option value="">
                              {selectedApartamento
                                ? "Seleccione un moderador"
                                : "Primero seleccione un apartamento"}
                            </option>
                            {residentes
                              .sort((a, b) => {
                                const tipoA = a.res_tipo_usuario || "";
                                const tipoB = b.res_tipo_usuario || "";
                                if (tipoA === "PR") return -1;
                                if (tipoB === "PR") return 1;
                                return 0;
                              })
                              .map((residente) => (
                                <option
                                  key={residente.res_email || residente.res_codigo}
                                  value={residente.res_email || ""}
                                >
                                  {residente.res_nombre_completo ||
                                    residente.res_nombre ||
                                    residente.res_email ||
                                    "Sin nombre"}
                                  {residente.res_tipo_usuario_nombre &&
                                    ` - ${residente.res_tipo_usuario_nombre}`}
                                  {residente.res_email && ` (${residente.res_email})`}
                                </option>
                              ))}
                          </select>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <Input
                        value={asambleaMostrar.asa_moderador_nombre}
                        disabled
                        className="bg-gray-50"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {asambleaMostrar.asa_moderador}
                      </p>
                    </>
                  )}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t">
                {isEditing ? (
                  <>
                    <Button
                      onClick={() => {
                        setIsEditing(false);
                        setTitulo(asambleaMostrar.asa_titulo);
                        setDescripcion(asambleaMostrar.asa_descripcion || "");
                        setSelectedModerador(asambleaMostrar.asa_moderador || "");
                      }}
                      variant="outline"
                      disabled={isSubmitting}
                      className="flex-1"
                    >
                      Cancelar
                    </Button>
                    <Button
                      onClick={handleSaveChanges}
                      disabled={isSubmitting}
                      className="flex-1 bg-military-500 hover:bg-military-600 text-white"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                          Guardando...
                        </>
                      ) : (
                        <>
                          <Save size={20} className="mr-2" />
                          Guardar Cambios
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => setIsEditing(true)}
                      variant="outline"
                      className="flex-1 border-military-500 text-military-600 hover:bg-military-50"
                    >
                      <Edit size={20} className="mr-2" />
                      Modificar
                    </Button>
                    <Button
                      onClick={() => onClose(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Cerrar
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
